import {
  ACCOUNT_TOKEN_RENEW_LEAD_MS,
  accountTokenNeedsRenewal,
  getAccountTokenExpiresAt,
} from "@/lib/account-token"
import { currentGuestToken, ensureGuestToken, GUEST_SESSION_EVENT } from "@/lib/guest-session"
import { renewAccountTokenIfDue } from "@/lib/renew-account-token"
import { useEffect } from "react"

/** Renews the customer token before it expires, including when a backgrounded tab becomes visible. */
export function useAccountTokenRenewal(): void {
  useEffect(() => {
    let timer = 0
    let cancelled = false

    function arm() {
      window.clearTimeout(timer)
      if (cancelled) return
      if (!currentGuestToken()) {
        void ensureGuestToken().then(token => { if (token && !cancelled) arm() })
        return
      }

      if (accountTokenNeedsRenewal()) {
        void renewAccountTokenIfDue().finally(() => {
          if (cancelled || accountTokenNeedsRenewal()) return
          arm()
        })
        return
      }

      const expiresAt = getAccountTokenExpiresAt()
      if (expiresAt === null) return
      const delay = expiresAt - ACCOUNT_TOKEN_RENEW_LEAD_MS - Date.now()
      timer = window.setTimeout(() => {
        void renewAccountTokenIfDue().finally(() => {
          if (!cancelled) arm()
        })
      }, Math.max(0, delay))
    }

    function onVisible() {
      if (document.visibilityState === "visible") arm()
    }

    arm()
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener(GUEST_SESSION_EVENT, arm)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener(GUEST_SESSION_EVENT, arm)
    }
  }, [])
}
